import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { toast } from 'react-toastify';
import {
  My_Store,
  Order_list,
  Set_Store,
  Store_Dashboard_Summary,
  sellerCancelLineItem,
  getOrderDetails,
  fetchOrderDetailsStatus,
} from '../../../Api';
import { StoreDashboardSummary } from '../../../Api/apis/types/store.types';
import { STORAGE_CONSTANTS } from '../../../shared/Constants/Constants';
import { Order } from '../../OrderList/types';
import { TimePeriod, OrderTab, TabCounts } from '../types';
import {
  isNewOrder,
  isPackingTabOrder,
  isInTransitOrder,
  isOthersOrder,
  getOrderStatus,
  mapOrderStatus,
} from '../utils';
import { checkIfStoreIsOpen } from '../../../Utils/checkStoreOpen';

export const useStoreDashboard = () => {
  const navigate = useNavigate();

  // Store profile info
  const [storeName, setStoreName] = useState<string | null>('');
  const [storeId, setStoreId] = useState<string | null>('');
  const [storeLogo, setStoreLogo] = useState<any>('');

  // Store operating hours & open/closed status
  const [openingTime, setOpeningTime] = useState<string | null>(null);
  const [closingTime, setClosingTime] = useState<string | null>(null);
  const [isStoreOpen, setIsStoreOpen] = useState<boolean>(false);

  // Time filter state (Today | 7 days | 30 days)
  const [timeFilter, setTimeFilter] = useState<TimePeriod>('7 days');
  const [fromDate, setFromDate] = useState<string>(
    moment().subtract(6, 'days').format('YYYY-MM-DD')
  );
  const [toDate, setToDate] = useState<string>(moment().format('YYYY-MM-DD'));

  // Dashboard summary metrics
  const [summary, setSummary] = useState<StoreDashboardSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState<boolean>(true);

  // Orders to handle state
  const [orders, setOrders] = useState<Order[]>([]);
  const [ordersLoading, setOrdersLoading] = useState<boolean>(false);
  const [activeOrderTab, setActiveOrderTab] = useState<OrderTab>('all');
  const [updatingOrderId, setUpdatingOrderId] = useState<string | null>(null);

  // Calculate dates based on selected time period
  const handlePeriodChange = useCallback((period: TimePeriod) => {
    setTimeFilter(period);
    const to = moment().format('YYYY-MM-DD');
    let from = to;

    if (period === '7 days') {
      from = moment().subtract(6, 'days').format('YYYY-MM-DD');
    } else if (period === '30 days') {
      from = moment().subtract(29, 'days').format('YYYY-MM-DD');
    }

    setFromDate(from);
    setToDate(to);
  }, []);

  // Fetch summary totals for active date range
  const fetchSummary = useCallback(
    async (from: string = fromDate, to: string = toDate) => {
      try {
        setSummaryLoading(true);
        const response = await Store_Dashboard_Summary(from, to);
        if (response?.data?.data) {
          setSummary(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching dashboard summary:', error);
      } finally {
        setSummaryLoading(false);
      }
    },
    [fromDate, toDate]
  );

  // Fetch orders to handle for active date range
  const fetchOrdersToHandle = useCallback(
    async (from: string = fromDate, to: string = toDate) => {
      try {
        setOrdersLoading(true);
        const userToken = localStorage.getItem(STORAGE_CONSTANTS.USER_TOKEN);
        const response = await Order_list(userToken, 1, from, to);
        if (
          response?.status === 200 &&
          Array.isArray(response.data?.order_list)
        ) {
          setOrders(response.data.order_list);
        }
      } catch (error) {
        console.error('Error fetching orders:', error);
      } finally {
        setOrdersLoading(false);
      }
    },
    [fromDate, toDate]
  );

  // Effect to calculate and update open/closed status periodically
  useEffect(() => {
    const updateOpenStatus = () => {
      if (openingTime && closingTime) {
        setIsStoreOpen(checkIfStoreIsOpen(openingTime, closingTime));
      } else {
        setIsStoreOpen(false);
      }
    };

    updateOpenStatus();
    const interval = setInterval(updateOpenStatus, 60000);
    return () => clearInterval(interval);
  }, [openingTime, closingTime]);

  // Fetch store details & auth check
  const fetchStoreInfo = useCallback(async () => {
    const userToken = localStorage.getItem(STORAGE_CONSTANTS.USER_TOKEN);
    if (!userToken) return;

    try {
      const response = await My_Store(userToken);
      if (response && response.status === 200) {
        const store = response.data?.my_stores?.[0];
        if (store) {
          setStoreName(store.store_name);
          setStoreId(String(store.id));
          setStoreLogo(store.logo);

          let openTime = store.opening_time ?? null;
          let closeTime = store.closing_time ?? null;

          if (!openTime || !closeTime) {
            try {
              const detailsRes = await Set_Store(store.id, userToken);
              const details = detailsRes?.data?.[0] || detailsRes?.data;
              if (details) {
                openTime = details.opening_time ?? openTime;
                closeTime = details.closing_time ?? closeTime;
              }
            } catch (e) {
              console.log('Error fetching store operating hours:', e);
            }
          }

          setOpeningTime(openTime);
          setClosingTime(closeTime);

          localStorage.setItem(STORAGE_CONSTANTS.STORE_NAME, store.store_name);
          localStorage.setItem(STORAGE_CONSTANTS.STORE_STATUS, store.admission);
          localStorage.setItem(STORAGE_CONSTANTS.STORE_ID, String(store.id));
          localStorage.setItem(STORAGE_CONSTANTS.STORE_LOGO, store.logo);
          localStorage.setItem(
            STORAGE_CONSTANTS.AREA_PIN,
            store.service_area_pin_code
          );
        }
      }
    } catch (error) {
      console.error('Error fetching My Store details:', error);
    }
  }, []);

  // Initial load
  useEffect(() => {
    window.scrollTo(0, 0);
    const storedName = localStorage.getItem(STORAGE_CONSTANTS.STORE_NAME);
    const storedId = localStorage.getItem(STORAGE_CONSTANTS.STORE_ID);
    const storedLogo = localStorage.getItem(STORAGE_CONSTANTS.STORE_LOGO);

    if (storedName) setStoreName(storedName);
    if (storedId) setStoreId(storedId);
    if (storedLogo) setStoreLogo(storedLogo);

    fetchStoreInfo();
  }, [fetchStoreInfo]);

  // Refetch summary and orders whenever date range changes
  useEffect(() => {
    fetchSummary(fromDate, toDate);
    fetchOrdersToHandle(fromDate, toDate);
  }, [fromDate, toDate, fetchSummary, fetchOrdersToHandle]);

  // Filter orders for active items (exclude delivered & cancelled from active list)
  const activeOrders = useMemo(() => {
    return orders.filter((o) => {
      const s = getOrderStatus(o);
      return s !== 'DELIVERED' && s !== 'COMPLETE' && s !== 'CANCELLED';
    });
  }, [orders]);

  // Counts for tabs
  const tabCounts = useMemo<TabCounts>(() => {
    return {
      all: activeOrders.length,
      new: activeOrders.filter(isNewOrder).length,
      packing: activeOrders.filter(isPackingTabOrder).length,
      in_transit: activeOrders.filter(isInTransitOrder).length,
      others: activeOrders.filter(isOthersOrder).length,
    };
  }, [activeOrders]);

  // Orders filtered by the active tab
  const displayedOrders = useMemo(() => {
    if (activeOrderTab === 'new') return activeOrders.filter(isNewOrder);
    if (activeOrderTab === 'packing')
      return activeOrders.filter(isPackingTabOrder);
    if (activeOrderTab === 'in_transit')
      return activeOrders.filter(isInTransitOrder);
    if (activeOrderTab === 'others')
      return activeOrders.filter(isOthersOrder);
    return activeOrders;
  }, [activeOrders, activeOrderTab]);

  // Action handler to advance order status
  const handleUpdateOrderStatus = useCallback(
    async (order: Order, action: 'PROCESSING' | 'READY') => {
      try {
        setUpdatingOrderId(order.id);

        // 1. Fetch line item product IDs via getOrderDetails or fallback to order_details
        let productIdsString = '';
        try {
          const details = await getOrderDetails(order.id);
          const productList = details?.product_list || [];
          if (productList.length > 0) {
            productIdsString = productList
              .map((p: any) => p?.product?.id)
              .filter((id: any) => id !== undefined)
              .join(',');
          }
        } catch (err) {
          console.warn('Failed to fetch order details, trying fallback:', err);
        }

        if (!productIdsString && order.order_details?.length) {
          productIdsString = order.order_details
            .map((d: any) => d.product?.id || d.id)
            .filter(Boolean)
            .join(',');
        }

        if (!productIdsString) {
          toast.error('Unable to retrieve line items for this order.');
          return;
        }

        // 2. Query available status options from backend to use exact choice string
        let nextStatus = '';
        try {
          const statusRes = await fetchOrderDetailsStatus();
          if (statusRes?.status && statusRes?.status_list) {
            const currentStatus = getOrderStatus(order);
            const mappedKey = mapOrderStatus(
              currentStatus,
              order.delivery_method || 'DELIVERY'
            );
            const availableStatuses: string[] =
              statusRes.status_list[mappedKey] || [];

            if (action === 'PROCESSING') {
              nextStatus =
                availableStatuses.find((s) => /processing/i.test(s)) ||
                'Processing';
            } else if (action === 'READY') {
              const fallback =
                order.delivery_method === 'PICKED_UP'
                  ? 'Ready to Pick'
                  : 'Ready to Ship';
              nextStatus =
                availableStatuses.find((s) => /ready/i.test(s)) || fallback;
            }
          }
        } catch (err) {
          console.warn(
            'Failed to fetch status list from backend, using fallback:',
            err
          );
        }

        if (!nextStatus) {
          if (action === 'PROCESSING') {
            nextStatus = 'Processing';
          } else {
            nextStatus =
              order.delivery_method === 'PICKED_UP'
                ? 'Ready to Pick'
                : 'Ready to Ship';
          }
        }

        // 3. Update order line items
        await sellerCancelLineItem(order.id, productIdsString, nextStatus);
        toast.success(`Order #${order.id} marked as ${nextStatus}!`);

        // 4. Refresh orders and summary to update UI state immediately
        await Promise.allSettled([
          fetchOrdersToHandle(fromDate, toDate),
          fetchSummary(fromDate, toDate),
        ]);
      } catch (error: any) {
        console.error('Error updating order status:', error);
        toast.error(
          error?.response?.data?.message || 'Failed to update order status.'
        );
      } finally {
        setUpdatingOrderId(null);
      }
    },
    [fetchOrdersToHandle, fetchSummary, fromDate, toDate]
  );

  // Delivery percentage for integrated orders donut
  const deliveryPercentage = useMemo(() => {
    const integratedDeliveryStats =
      summary?.total_integrated_order_with_breakdown;
    if (!integratedDeliveryStats?.total || integratedDeliveryStats.total === 0)
      return 0;
    return Math.min(
      100,
      Math.round(
        ((integratedDeliveryStats.synced || 0) /
          integratedDeliveryStats.total) *
          100
      )
    );
  }, [summary?.total_integrated_order_with_breakdown]);

  // Navigation handlers
  const handleNavigateOutOfStock = useCallback(() => {
    navigate('/products?prodStatus=out_of_stock');
  }, [navigate]);

  const handleNavigateLowStock = useCallback(() => {
    navigate('/products?prodStatus=low_stock');
  }, [navigate]);

  const handleViewAllOrders = useCallback(() => {
    navigate('/orders');
  }, [navigate]);

  const handleViewAllIntegratedOrders = useCallback(() => {
    navigate('/integrated-orders');
  }, [navigate]);

  const handleAddProduct = useCallback(() => {
    navigate('/add-product');
  }, [navigate]);

  const handleViewProducts = useCallback(() => {
    navigate('/products');
  }, [navigate]);

  const handleCreateOffer = useCallback(() => {
    navigate('/create-offer');
  }, [navigate]);

  const handleEditStorePage = useCallback(() => {
    navigate('/set-store');
  }, [navigate]);

  const refreshOrders = useCallback(async () => {
    await Promise.allSettled([
      fetchOrdersToHandle(fromDate, toDate),
      fetchSummary(fromDate, toDate),
    ]);
  }, [fetchOrdersToHandle, fetchSummary, fromDate, toDate]);

  return {
    storeName,
    storeLogo,
    timeFilter,
    isStoreOpen,
    openingTime,
    closingTime,
    summary,
    summaryLoading,
    storeId,
    ordersLoading,
    activeOrderTab,
    updatingOrderId,
    tabCounts,
    displayedOrders,
    statsLoading: summaryLoading,
    deliveryPercentage,
    setActiveOrderTab,
    handlePeriodChange,
    handleUpdateOrderStatus,
    handleNavigateOutOfStock,
    handleNavigateLowStock,
    handleViewAllOrders,
    handleViewAllIntegratedOrders,
    handleAddProduct,
    handleViewProducts,
    handleCreateOffer,
    handleEditStorePage,
    refreshOrders,
  };
};
